import { useApiQuery } from '@/hooks/useApiQuery';
import { apiGet, contactEndpoints, operationsClient } from '@/lib/api';
import { contactKeys } from '@/lib/queryKeys';
import type { Membership } from '@/types/membership';
import type { Equipment } from '@/types/equipment';

export function equipmentMemberships(memberships: Membership[], equipment: Pick<Equipment, 'id' | 'customerId' | 'serviceAddressId'>, now = Date.now()) {
  return memberships.filter((membership) => {
    if (membership.customerId !== equipment.customerId) return false;
    const snapshot = membership.planSnapshot;
    // Sold coverage selections are authoritative, including an empty array.
    const ids = Array.isArray(snapshot.coverageSelections)
      ? snapshot.coverageSelections.flatMap((selection) =>
        selection && typeof selection === 'object' && Array.isArray(selection.equipmentIds) ? selection.equipmentIds : [])
      : Array.isArray(snapshot.selectedEquipmentIds) ? snapshot.selectedEquipmentIds : [];
    return ids.includes(equipment.id);
  }).map((membership) => ({
    membership,
    covered: (!membership.serviceLocationId || membership.serviceLocationId === equipment.serviceAddressId)
      && (membership.status === 'ACTIVE' || membership.status === 'PAST_DUE'
        || (membership.status === 'CANCELLED' && !!membership.cancellationEffectiveDate
          && new Date(membership.cancellationEffectiveDate).getTime() > now)),
  }));
}

export function useEquipmentMemberships(equipment: Equipment | undefined, enabled: boolean) {
  return useApiQuery({
    queryKey: [...contactKeys.membershipsBase(equipment?.customerId ?? ''), 'equipment', equipment?.id, equipment?.serviceAddressId],
    enabled: enabled && !!equipment,
    queryFn: async () => {
      const memberships: Membership[] = [];
      for (let page = 1; ; page++) {
        const result = await apiGet<{ data: Membership[]; hasMore: boolean }>(
          operationsClient, contactEndpoints.memberships(equipment!.customerId),
          { params: { page, limit: 100 } },
        );
        memberships.push(...result.data);
        if (!result.hasMore) break;
      }
      return equipmentMemberships(memberships, equipment!);
    },
    staleTime: 0,
    showErrorToast: false,
  });
}
